import { useState } from 'react';
import { personApi } from '@/services/person-api';
import { getErrorMessage } from '@/lib/utils';
import { Dialog, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import type { ReconcileTarget } from './reconcile-target';

interface MarkAsBotConfirmDialogProps {
  open: boolean;
  target: ReconcileTarget | null;
  onClose: () => void;
  onConfirmed: () => void;
}

function sourceLabel(source: ReconcileTarget['source']): string {
  return source === 'github' ? 'GitHub' : 'Slack';
}

async function markAsBotForSource(target: ReconcileTarget): Promise<unknown> {
  if (target.source === 'github') {
    return personApi.markGitHubAsBot({ login: target.sourceAccountId });
  }
  return personApi.markSlackAsBot({ slackId: target.sourceAccountId });
}

export function MarkAsBotConfirmDialog({
  open,
  target,
  onClose,
  onConfirmed,
}: Readonly<MarkAsBotConfirmDialogProps>) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!target) {
    return <Dialog open={open} onClose={onClose} title="Mark as bot"><p /></Dialog>;
  }

  const label = sourceLabel(target.source);
  const isConfirmedBot = target.isBot;

  const handleConfirm = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      await markAsBotForSource(target);
      onConfirmed();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to mark as bot'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Mark ${label} account as bot`}>
      <div className="space-y-3 text-sm">
        <div className="rounded-md border bg-muted/40 p-3">
          <div className="font-medium">{target.displayName}</div>
          <div className="text-xs text-muted-foreground">
            {target.email ?? '—'} · {target.sourceAccountId}
          </div>
        </div>

        {isConfirmedBot && (
          <p>
            Creates a new bot <code>Person</code> and links this {label} account to it. Bots are
            hidden by default in the People list.
          </p>
        )}
        {!isConfirmedBot && (
          <p className="rounded-md border border-warning-200 bg-warning-50 p-3 text-warning-700">
            <strong>Heads up:</strong> {label} does not flag this account as a bot. Marking it
            will still create a bot Person. Continue only if you're sure.
          </p>
        )}

        {error && <p className="text-sm text-error-700">{error}</p>}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleConfirm} isLoading={submitting} disabled={submitting}>
          Mark as bot
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
